import React, { useEffect, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { setBackButtonUrl } from "../uiSlice";
import { GAME_UPLOAD_PATH } from "../../utils/playerNavigation";
import {
  themeButtonBackground,
  themeButtonTextColor,
} from "../../utils/colorUtils";
import {
  ensureFontsFromSectionStyles,
  parseSectionStyles,
  textStyleToCss,
} from "../../utils/sectionStyles";
import { ensureGoogleFont } from "../../admin/components/TextStyleControls";
import icon1 from "../../img/icon1.svg";
import icon2 from "../../img/icon2.png";
import icon3 from "../../img/icon3.png";
import "../../components/stage/stageShell.css";
import "./howToPlay.css";

function asRuleList(value) {
  if (Array.isArray(value) && value.length) return value.map((v) => String(v ?? ""));
  if (typeof value === "string" && value.trim()) {
    try {
      const p = JSON.parse(value);
      if (Array.isArray(p) && p.length) return p.map((v) => String(v ?? ""));
    } catch {
      /* ignore */
    }
  }
  return [];
}

const CARDS = [
  {
    key: "submit_your_talent_rules",
    titleKey: "submit_your_talent_title",
    styleId: "submit_your_talent",
    num: "01",
    defaultTitle: "Submit your talent",
    accent: "#e25569",
    icon: icon1,
    iconAlt: "Megaphone",
  },
  {
    key: "expolre_and_react_rules",
    titleKey: "expolre_and_react_title",
    styleId: "expolre_and_react",
    num: "02",
    defaultTitle: "Explore & react",
    accent: "#3b82f6",
    icon: icon2,
    iconAlt: "Reactions",
  },
  {
    key: "cast_your_vote_rules",
    titleKey: "cast_your_vote_title",
    styleId: "cast_your_vote",
    num: "03",
    defaultTitle: "Cast your vote",
    accent: "#22a06b",
    icon: icon3,
    iconAlt: "Trophy",
  },
];

export default function HowToPlay() {
  const dispatch = useDispatch();
  const themeData = useSelector((s) => s.theme.data);
  const [searchParams] = useSearchParams();
  const mobileAppQuery =
    searchParams.get("fromMobileApp") === "true" ? "?fromMobileApp=true" : "";

  useEffect(() => {
    dispatch(setBackButtonUrl(GAME_UPLOAD_PATH));
  }, [dispatch]);

  const isMobile =
    typeof window !== "undefined" && window.matchMedia("(max-width: 768px)").matches;

  const sectionStyles = useMemo(() => {
    const styles = parseSectionStyles(themeData?.section_styles, {
      font_family: themeData?.font_family,
      font_size_desktop: themeData?.font_size_desktop,
      font_size_mobile: themeData?.font_size_mobile,
      rules_text_color: themeData?.rules_text_color,
      text_color: themeData?.text_color,
    });
    ensureFontsFromSectionStyles(styles, ensureGoogleFont);
    return styles;
  }, [themeData]);

  const cards = useMemo(
    () =>
      CARDS.map((c) => ({
        ...c,
        title: themeData?.[c.titleKey] || c.defaultTitle,
        rules: asRuleList(themeData?.[c.key]),
        titleStyle: textStyleToCss(sectionStyles[c.styleId]?.title, { isMobile }),
        rulesStyle: textStyleToCss(sectionStyles[c.styleId]?.rules, { isMobile }),
      })),
    [themeData, sectionStyles, isMobile]
  );

  const ctaBg = themeButtonBackground(themeData);
  const ctaColor = themeButtonTextColor(themeData);

  return (
    <div className="stage-shell htp-page">
      <div className="stage-inner">
        <h1 className="stage-title htp-title">How to play?</h1>
        <div className="htp-cards">
          {cards.map((card) => (
            <article
              key={card.key}
              className="htp-card"
              style={{ borderColor: card.accent }}
            >
              <div className="htp-card-head">
                <span
                  className="htp-num"
                  style={{ background: card.accent }}
                >
                  {card.num}
                </span>
                <h2
                  style={{
                    color: card.accent,
                    ...(card.titleStyle || null),
                  }}
                >
                  {card.title}
                </h2>
              </div>
              <ul className="htp-rules" style={card.rulesStyle || undefined}>
                {(card.rules.length
                  ? card.rules
                  : ["Rules will appear here once configured."]
                ).map((rule, i) => (
                  <li key={`${card.key}-${i}`}>
                    <span className="htp-star" aria-hidden>
                      ★
                    </span>
                    <span>{rule}</span>
                  </li>
                ))}
              </ul>
              <img
                className="htp-card-icon"
                src={card.icon}
                alt={card.iconAlt}
              />
            </article>
          ))}
        </div>
        <div className="htp-ready">
          <p className="htp-ready-label">✦ Ready? ✦</p>
          <Link
            to={`/game${mobileAppQuery}`}
            className="stage-cta htp-cta"
            style={{
              ...(ctaBg ? { background: ctaBg } : null),
              ...(ctaColor ? { color: ctaColor } : null),
            }}
          >
            Let&apos;s go! →
          </Link>
        </div>
      </div>
    </div>
  );
}
